/**
 * Gift Wrap Functionality
 * Handles adding/removing gift wrap and ribbon from cart
 */

class GiftWrapManager {
  constructor() {
    this.ribbonProductId = null;
    this.ribbonVariantId = null;
    this.busy = false;
    this._loadRibbonConfig();
    this.init();
  }

  _loadRibbonConfig() {
    const configEl = document.querySelector('script[data-gift-wrap-config]');
    if (configEl) {
      try {
        const config = JSON.parse(configEl.textContent);
        this.ribbonProductId = config.ribbonProductId ? String(config.ribbonProductId) : null;
        this.ribbonVariantId = config.ribbonVariantId ? String(config.ribbonVariantId) : null;
      } catch (e) {
        console.error('Failed to parse gift wrap config:', e);
      }
    }
  }

  init() {
    this.bindEvents();
    this.updateGiftWrapVisibility();

    // Initial check after a delay to ensure cart is loaded
    setTimeout(() => {
      this.checkAndRemoveGiftWrapIfNeeded();
    }, 1000);
  }

  bindEvents() {
    var onCartChange = this.debounce(() => {
      this.updateGiftWrapVisibility();
      this.checkAndRemoveGiftWrapIfNeeded();
    }, 800);

    ['cart:quantity-updated', 'cart:updated', 'cart:rerendered',
     'cart-drawer:updated', 'cart:item:removed', 'cart:item:added'
    ].forEach(evt => document.addEventListener(evt, onCartChange));

    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-gift-wrap-checkbox]')) {
        this.handleGiftWrapToggle(e.target);
      }
    });

    document.addEventListener('click', (e) => {
      var btn = e.target.closest('[data-gift-wrap-checkbox]');
      if (btn && btn.tagName === 'BUTTON') {
        this.handleGiftWrapButtonToggle(btn);
      }
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-remove-button]') || e.target.closest('remove-item')) {
        setTimeout(onCartChange, 1000);
      }
    });
  }

  debounce(fn, ms) {
    var timer;
    return function() {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
  }

  async handleGiftWrapToggle(checkbox) {
    if (this.busy) return;
    var variantId = checkbox.dataset.variantId;
    var productId = checkbox.dataset.productId;
    var isChecked = checkbox.checked;

    if (!productId || !variantId) {
      checkbox.checked = !isChecked;
      return;
    }

    try {
      if (isChecked) {
        await this.addGiftWrapToCart(variantId);
      } else {
        await this.removeGiftWrapFromCart(productId);
      }
    } catch (error) {
      checkbox.checked = !isChecked;
    }
  }

  async handleGiftWrapButtonToggle(button) {
    if (this.busy) return;
    var productId = button.dataset.productId;
    var variantId = button.dataset.variantId;
    var isActive = button.dataset.giftWrapActive === 'true';

    if (!productId || !variantId) return;

    try {
      if (!isActive) {
        await this.addGiftWrapToCart(variantId);
      } else {
        await this.removeGiftWrapFromCart(productId);
      }
    } catch (error) {
      console.error('Error toggling gift wrap:', error);
    }
  }

  async addGiftWrapToCart(variantId) {
    this.busy = true;
    var items = [{
      id: parseInt(variantId, 10),
      quantity: 1,
      properties: { '_gift_wrap': 'true' }
    }];

    if (this.ribbonVariantId) {
      items.push({
        id: parseInt(this.ribbonVariantId, 10),
        quantity: 1,
        properties: { '_gift_wrap_ribbon': 'true' }
      });
    }

    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });

      if (!response.ok) {
        var errorData = await response.json().catch(() => ({}));
        console.error('Cart add error:', response.status, errorData);
        throw new Error('Failed to add gift wrap: ' + response.status);
      }

      await this.refreshPage();
    } catch (error) {
      console.error('Error adding gift wrap:', error);
      this.showMessage('Failed to add gift wrapping. Please try again.', 'error');
      throw error;
    } finally {
      this.busy = false;
    }
  }

  async removeGiftWrapFromCart(productId) {
    this.busy = true;
    try {
      var cart = await this.fetchCart();

      var giftWrapItem = cart.items.find(item =>
        item.product_id.toString() === productId &&
        item.properties && item.properties._gift_wrap
      );
      if (!giftWrapItem) throw new Error('Gift wrap item not found in cart');

      // Build updates to remove gift wrap and all companion ribbon items in one call
      var updates = {};
      updates[giftWrapItem.key] = 0;

      // Find ribbon items by property tag or by product ID
      var ribbonItems = this._findRibbonItems(cart, giftWrapItem.key);
      ribbonItems.forEach(function(item) {
        updates[item.key] = 0;
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });

      await this.refreshPage();
    } catch (error) {
      console.error('Error removing gift wrap:', error);
      this.showMessage('Failed to remove gift wrapping. Please try again.', 'error');
      throw error;
    } finally {
      this.busy = false;
    }
  }

  /**
   * Finds ribbon items in the cart by _gift_wrap_ribbon property or by product ID
   */
  _findRibbonItems(cart, excludeKey) {
    var self = this;
    return cart.items.filter(function(item) {
      if (excludeKey && item.key === excludeKey) return false;
      if (item.properties && item.properties._gift_wrap_ribbon) return true;
      if (self.ribbonProductId && item.product_id.toString() === self.ribbonProductId) return true;
      return false;
    });
  }

  /**
   * Checks if an item is a gift-wrap companion (gift wrap or its ribbon)
   */
  _isGiftWrapOrRibbon(item) {
    if (item.properties && item.properties._gift_wrap) return true;
    if (item.properties && item.properties._gift_wrap_ribbon) return true;
    if (this.ribbonProductId && item.product_id.toString() === this.ribbonProductId) return true;
    return false;
  }

  async fetchCart() {
    var response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
    return response.json();
  }

  async refreshPage() {
    document.dispatchEvent(new CustomEvent('cart:quantity-updated', { detail: document }));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    setTimeout(() => this.adjustCartCount(), 1000);
  }

  async adjustCartCount() {
    try {
      var cart = await this.fetchCart();
      var ribbonQty = 0;
      cart.items.forEach(function(item) {
        if (item.properties && item.properties._gift_wrap_ribbon) {
          ribbonQty += item.quantity;
        }
      });
      if (ribbonQty === 0) return;

      var adjusted = cart.item_count - ribbonQty;
      document.querySelectorAll('[data-cart-count]').forEach(function(el) {
        el.textContent = adjusted;
        el.classList.toggle('opacity-0', adjusted < 1);
        el.classList.toggle('opacity-100', adjusted >= 1);
      });
    } catch (e) {
      // silently fail
    }
  }

  updateGiftWrapVisibility() {
    var sections = document.querySelectorAll('[data-gift-wrap-section]');
    sections.forEach(section => {
      var element = section.querySelector('[data-gift-wrap-checkbox]');
      if (element) this.updateGiftWrapState(element);
    });
  }

  async updateGiftWrapState(element) {
    try {
      var cart = await this.fetchCart();
      var productId = element.dataset.productId;
      var inCart = cart.items.some(item =>
        item.product_id.toString() === productId &&
        item.properties && item.properties._gift_wrap
      );

      if (element.tagName === 'INPUT') {
        element.checked = inCart;
      } else if (element.tagName === 'BUTTON') {
        element.dataset.giftWrapActive = inCart ? 'true' : 'false';
        element.textContent = inCart ? 'Remove' : 'Add';
      }
    } catch (error) {
      console.error('Error updating gift wrap state:', error);
    }
  }

  /**
   * Checks if gift wrap should be automatically removed.
   * Removes gift wrap + ribbon when no other products remain.
   * Also removes orphaned ribbons (ribbon in cart without gift wrap).
   */
  async checkAndRemoveGiftWrapIfNeeded() {
    try {
      var cart = await this.fetchCart();

      var giftWrapItems = cart.items.filter(item =>
        item.properties && item.properties._gift_wrap
      );
      var ribbonItems = this._findRibbonItems(cart);
      var nonGiftWrapItems = cart.items.filter(item => !this._isGiftWrapOrRibbon(item));

      var updates = {};

      // If no real items remain, remove all gift wrap and ribbon items
      if (nonGiftWrapItems.length === 0 && cart.items.length > 0) {
        giftWrapItems.forEach(function(item) { updates[item.key] = 0; });
        ribbonItems.forEach(function(item) { updates[item.key] = 0; });
      } else if (giftWrapItems.length === 0 && ribbonItems.length > 0) {
        // Orphaned ribbon: gift wrap was removed but ribbon stayed
        ribbonItems.forEach(function(item) { updates[item.key] = 0; });
      }

      if (Object.keys(updates).length > 0) {
        console.log('Auto-removing gift wrap/ribbon items:', updates);
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
        await this.refreshPage();
      }
    } catch (error) {
      console.error('Error checking gift wrap removal:', error);
    }
  }

  showMessage(message, type) {
    var el = document.createElement('div');
    el.className = 'gift-wrap-message fixed top-4 right-4 p-3 rounded shadow-lg z-50 ' +
      (type === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800');
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 3000);
  }
}

window.giftWrapManager = new GiftWrapManager();

document.addEventListener('shopify:section:load', function() {
  setTimeout(function() { window.giftWrapManager = new GiftWrapManager(); }, 100);
});
